/**
 * The screen orientation lock: off, portrait or landscape.
 *
 * Android only. A browser tab can't lock orientation outside fullscreen, so on
 * the web every function here answers null and Settings doesn't offer it.
 *
 * The choice lives natively (OrientationPlugin, SharedPreferences) and
 * MainActivity applies it at launch, so there is nothing to restore from here
 * on start-up — this file only reads it for Settings and changes it.
 */

import { registerPlugin } from '@capacitor/core'

import { withTimeoutOr } from '../lib/timeout'
import { platform } from './platform'

export type OrientationMode = 'off' | 'portrait' | 'landscape'

export const ORIENTATION_MODES: readonly OrientationMode[] = ['off', 'portrait', 'landscape']

type OrientationPlugin = {
  get(): Promise<{ mode: string }>
  set(options: { mode: OrientationMode }): Promise<{ mode: string }>
}

/**
 * Registered statically, like RestAlarm in native/notify.ts: `registerPlugin`
 * returns the proxy synchronously, so no promise ever wraps the plugin object
 * — the shape that hung every plugin in this app (see lib/unthenable).
 */
const Orientation = registerPlugin<OrientationPlugin>('Orientation')

function hasOrientation(): boolean {
  return platform() === 'android'
}

/** Anything the native side sends that isn't a known mode reads as unlocked. */
function normalise(mode: unknown): OrientationMode {
  return (ORIENTATION_MODES as readonly unknown[]).includes(mode)
    ? (mode as OrientationMode)
    : 'off'
}

/** The saved lock, or null off Android or when the plugin doesn't answer. */
export async function getOrientationLock(): Promise<OrientationMode | null> {
  if (!hasOrientation()) return null
  return withTimeoutOr(
    Orientation.get().then((r) => normalise(r.mode)),
    4000,
    null,
    'Orientation.get',
  )
}

/** Save and apply a lock. Resolves to the mode now in force, or null on failure. */
export async function setOrientationLock(
  mode: OrientationMode,
): Promise<OrientationMode | null> {
  if (!hasOrientation()) return null
  return withTimeoutOr(
    Orientation.set({ mode }).then((r) => normalise(r.mode)),
    4000,
    null,
    'Orientation.set',
  )
}
